import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ChangesAction } from 'app/pages/sliicer/shared/components/updates-widget/updates-widget.models';
import { Debounce } from 'app/pages/sliicer/shared/directives/debounce/debounce.directive';
import { validateRule } from 'app/pages/sliicer/shared/utils/base-validation';
import { SliicerCaseStudy } from 'app/shared/models/sliicer';
import { Regime } from 'app/shared/models/sliicer/settings';
import { DateutilService } from 'app/shared/services/dateutil.service';
import { TrackBy } from 'app/shared/utils/track-by';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import moment from 'moment';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import {
    calculateTimelineMarkerLayout,
    TimelineMarkerAlignment,
    TimelineMarkerAnchor,
} from './regime-settings-timeline.utils';

const dateFormat = 'YYYY-MM-DD';
const validationRules: RegimeValidation = { name: 'validString', periodEnd: 'validDate' };
const millisecondsInDay: number = 24 * 60 * 60 * 1000;
const timelineMarkerLaneGap = 2;
const timelineMarkerLaneHeight = 18;
const timelineMarkerLineOffset = 1;
const timelineMarkerTopOffset = 5;

export interface TimelineElement {
    name: string;
    size: number;
    periodEnd: string;
}

export interface TimelineMarker extends TimelineMarkerAnchor {
    date: string;
    dateTime: string;
    labelOffset: number;
    labelTop: number;
    lane: number;
}

export interface ValidationData {
    validated: boolean;
    errors: Record<string, string>;
}

export interface RegimeValidation {
    name: string & 'validString';
    periodEnd: string & 'validDate';
}

@Component({
    selector: 'app-regime-settings',
    templateUrl: './regime-settings.component.html',
    styleUrls: ['./regime-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegimeSettingsComponent implements AfterViewChecked, AfterViewInit, OnChanges, OnDestroy, OnInit {
    @ViewChild('regimesForm') public regimesForm: NgForm;
    @ViewChild('timeline', { read: ElementRef }) private timeline: ElementRef<HTMLElement> | undefined;
    @ViewChildren('timelineMarkerLabel', { read: ElementRef })
    private timelineMarkerLabels: QueryList<ElementRef<HTMLElement>> | undefined;
    @Input() public isStudyLocked: boolean;
    @Input() public caseStudyDetails: SliicerCaseStudy;
    @Input() public changesAction: ChangesAction;
    @Output() public checkChanges: EventEmitter<string> = new EventEmitter();
    public regimes: Regime[];
    public caseStudyStartDate: string;
    public caseStudyEndDate: string;
    public timelineElements: TimelineElement[] = [];
    public timelineMarkers: TimelineMarker[] = [];
    public timelineMarkerMaximumLabelWidth: number | null = null;
    public timelineMarkerRailHeight = timelineMarkerTopOffset + timelineMarkerLaneHeight;
    public timelineMarkersPositioned = false;
    public readonly timelineMarkerLineOffset = timelineMarkerLineOffset;
    public errors: Record<string, string> = {};
    public activeChanges = false;
    public dateFormat = '';

    public error = false;
    public errorType = {
        regimeComplete: false,
    };
    public debounceData: Debounce = { value: 200, initial: true };
    public trackByIndex = TrackBy.byIndex;

    private dateFormatSubscription: Subscription | null = null;
    private timelineMarkerLabelsSubscription: Subscription | null = null;
    private timelineMarkerLayoutFrame: number | null = null;
    private timelineResizeObserver: ResizeObserver | null = null;

    constructor(
        private uiUtilsService: UiUtilsService,
        private cdr: ChangeDetectorRef,
        private dateutilService: DateutilService,
        private ngZone: NgZone,
    ) {}

    public ngOnInit(): void {
        this.dateFormatSubscription = this.dateutilService.dateFormat.subscribe(() => {
            this.dateFormat = this.dateutilService.getFormat();
            this.scheduleTimelineMarkerLayout();
            this.cdr.markForCheck();
        });
    }

    public ngAfterViewInit(): void {
        if (!this.timeline || !this.timelineMarkerLabels) {
            return;
        }

        if (typeof ResizeObserver !== 'undefined') {
            this.timelineResizeObserver = new ResizeObserver(() => this.scheduleTimelineMarkerLayout());
        }

        this.timelineMarkerLabelsSubscription = this.timelineMarkerLabels.changes.subscribe(() => {
            this.observeTimelineLayout();
            this.scheduleTimelineMarkerLayout();
        });
        this.observeTimelineLayout();
        this.scheduleTimelineMarkerLayout();
    }

    public ngAfterViewChecked(): void {
        if (!this.timelineMarkersPositioned && this.timelineMarkerLayoutFrame === null) {
            this.scheduleTimelineMarkerLayout();
        }
    }

    public ngOnDestroy(): void {
        this.dateFormatSubscription?.unsubscribe();
        this.timelineMarkerLabelsSubscription?.unsubscribe();
        this.timelineResizeObserver?.disconnect();

        if (this.timelineMarkerLayoutFrame !== null) {
            window.cancelAnimationFrame(this.timelineMarkerLayoutFrame);
        }
    }

    @HostListener('window:resize')
    public onWindowResize(): void {
        this.scheduleTimelineMarkerLayout();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.caseStudyDetails) {
            this.caseStudyStartDate = (this.caseStudyDetails.config && this.caseStudyDetails.config.startDate) || '';
            this.caseStudyEndDate = (this.caseStudyDetails.config && this.caseStudyDetails.config.endDate) || '';
            this.setRegimes();
            this.setTimelineElements();
        }
        if (changes.changesAction && this.getChangeValue()) {
            if (this.changesAction.action === 'undoChanges') {
                this.undoChanges();
                this.checkChanges.emit('');
            }
        }
    }

    public onDebounce() {
        if (this.regimesForm.valid) {
            this.checkChanges.emit('');
        } else {
            let shouldRedraw = false;

            const formControlKeys = Object.keys(this.regimesForm.controls);

            formControlKeys.forEach((key) => {
                const ctr = this.regimesForm.controls[key];

                if (ctr.valid === false) {
                    ctr.markAsDirty();
                    ctr.markAsTouched();
                    shouldRedraw = true;
                }
            });

            if (shouldRedraw) {
                this.uiUtilsService.safeChangeDetection(this.cdr);
            }
        }
    }

    // This method will handle regime name change
    public regimeNameChange(index: number): void {
        this.errors[index + '.name'] = validateRule({
            rule: validationRules.name,
            value: this.regimes[index].name || '',
        });

        if (this.regimesForm.valid) {
            this.setTimelineElements();
        }
    }

    // This method will handle regime name change
    public regimePeriodEndChange(index: number): void {
        this.errors[index + '.periodEnd'] = validateRule({
            rule: validationRules.periodEnd,
            value: this.regimes[index].periodEnd || '',
        });

        this.setRegimesPeriods();
        if (this.regimesForm.valid) {
            this.setTimelineElements();
        }
    }

    // This method will remove selected regime
    public removeRegime(index: number): void {
        this.regimes.splice(index, 1);
        this.setRegimesPeriods();
        this.setTimelineElements();
        this.checkChanges.emit('');
    }

    // This method will add new regime object if the array is validated
    public addRegime(): void {
        const validation: ValidationData = this.validateRegimes();
        this.errors = validation.errors;
        if (validation.validated) {
            let valid = true;
            if (this.regimes.length > 0) {
                const lastRegimeEnd = moment(this.regimes[this.regimes.length - 1].periodEnd)
                    .endOf('day')
                    .valueOf();
                const endOfCaseStudy = moment(this.caseStudyEndDate).endOf('day').valueOf();
                valid = lastRegimeEnd < endOfCaseStudy;
            }

            if (valid) {
                this.regimes.push({ name: '', periodStart: '', periodEnd: '', minPeriodStart: '' });
                this.setRegimesPeriods();
                this.setTimelineElements();
            }
        }
    }

    // This method will return regimes changes
    public getChanges(): Regime[] {
        const validation: ValidationData = this.validateRegimes();
        this.errors = validation.errors;
        if (validation.validated) {
            this.regimes.map((r) => {});
            return this.formatRegimePeriods(this.regimes);
        }
        return null;
    }

    // This method will undo all changes
    public undoChanges(): void {
        this.setRegimes();
        this.errors = this.validateRegimes().errors;
        this.setTimelineElements();
    }

    // This method will return the changes value
    public getChangeValue(): boolean {
        this.errorType.regimeComplete = false;

        if (this.regimes && this.regimes.length > 0) {
            if (
                (this.regimes.length == 1 && this.regimes.find((a) => a.periodEnd == '')) ||
                this.regimes.find(
                    (a) =>
                        moment(a.periodEnd).format(dateFormat) ==
                        moment(this.caseStudyDetails.config.endDate).format(dateFormat),
                )
            ) {
                this.errorType.regimeComplete = false;
            } else {
                this.errorType.regimeComplete = true;
            }
        }

        this.updateError();

        const caseStudyRegimes = (this.caseStudyDetails.settings && this.caseStudyDetails.settings.regimes) || [];
        const regimes = this.formatRegimePeriods(this.regimes);
        regimes.forEach(function (v) {
            delete v.minPeriodStart;
        });
        const caseStudyRegs = this.formatRegimePeriods(caseStudyRegimes);
        caseStudyRegs.forEach(function (v) {
            delete v.minPeriodStart;
        });
        return !_.isEqual(regimes, caseStudyRegs) && !this.error;
    }

    private updateError() {
        this.error = this.errorType.regimeComplete;
    }

    // This method will return formated regime periods
    private formatRegimePeriods(regimes: Regime[]): Regime[] {
        return regimes.map((r) => {
            return {
                name: r.name,
                periodStart: moment(r.periodStart).format(dateFormat),
                periodEnd: moment(r.periodEnd).format(dateFormat),
                minPeriodStart: moment(r.minPeriodStart).format(dateFormat),
            };
        });
    }

    // This method will adjust regime periods
    private setRegimesPeriods(): void {
        for (let i = 0; i < this.regimes.length; i++) {
            if (i === 0) {
                this.regimes[i].periodStart = this.caseStudyStartDate;
                this.regimes[i].minPeriodStart = this.caseStudyStartDate;
            } else if (this.regimes[i - 1].periodEnd) {
                this.regimes[i].periodStart = moment(this.regimes[i - 1].periodEnd)
                    .add(1, 'days')
                    .startOf('day')
                    .format(dateFormat);
                this.regimes[i].minPeriodStart = moment(this.regimes[i - 1].periodEnd)
                    .add(2, 'days')
                    .startOf('day')
                    .format(dateFormat);
            }
        }
    }

    // This method will validate the regimes array
    private validateRegimes(): ValidationData {
        const validation: ValidationData = { validated: true, errors: {} };
        for (let i = 0; i < this.regimes.length; i++) {
            for (const key in validationRules) {
                validation.errors[i + '.' + key] = validateRule({
                    rule: validationRules[key],
                    value: this.regimes[i][key] || '',
                });
                if (validation.errors[i + '.' + key] !== '' && validation.validated) {
                    validation.validated = false;
                }
            }
        }
        return validation;
    }

    // This method will set regimes
    private setRegimes(): void {
        const caseStudyRegimes = (this.caseStudyDetails.settings && this.caseStudyDetails.settings.regimes) || [];
        this.regimes = JSON.parse(JSON.stringify(caseStudyRegimes));
    }

    // This method will check weather or not there are active changes in the regimes
    private setActiveChanges(): void {
        this.activeChanges = this.getChangeValue();
    }

    // This method will set the timeline elements and will call the active changes method
    private setTimelineElements(): void {
        this.timelineElements = this.getTimelineElements(this.regimes, this.caseStudyStartDate, this.caseStudyEndDate);
        this.setTimelineMarkers();
        this.setActiveChanges();
    }

    private setTimelineMarkers(): void {
        const timelineMarkers = this.getTimelineMarkers(
            this.timelineElements,
            this.caseStudyStartDate,
            this.caseStudyEndDate,
        );
        const canReuseLayout =
            this.timelineMarkersPositioned &&
            timelineMarkers.length === this.timelineMarkers.length &&
            timelineMarkers.every((timelineMarker, index) => {
                const currentMarker = this.timelineMarkers[index];
                return (
                    timelineMarker.alignment === currentMarker.alignment &&
                    timelineMarker.date === currentMarker.date &&
                    timelineMarker.offset === currentMarker.offset
                );
            });

        this.timelineMarkers = timelineMarkers.map((timelineMarker, index) => {
            if (!canReuseLayout) {
                return timelineMarker;
            }

            return {
                ...timelineMarker,
                labelOffset: this.timelineMarkers[index].labelOffset,
                labelTop: this.timelineMarkers[index].labelTop,
                lane: this.timelineMarkers[index].lane,
            };
        });

        if (!canReuseLayout) {
            this.timelineMarkersPositioned = false;
            this.timelineMarkerRailHeight = timelineMarkerTopOffset + timelineMarkerLaneHeight;
            this.scheduleTimelineMarkerLayout();
        }
    }

    private getTimelineMarkers(
        timelineElements: TimelineElement[],
        studyStartDate: string,
        studyEndDate: string,
    ): TimelineMarker[] {
        const timelineMarkers: TimelineMarker[] = [];
        let offset = 0;

        if (studyStartDate) {
            timelineMarkers.push(this.createTimelineMarker('start', studyStartDate, 0));
        }

        timelineElements.forEach((timelineElement) => {
            offset += timelineElement.size;

            if (!moment(timelineElement.periodEnd).isSame(moment(studyEndDate), 'day')) {
                timelineMarkers.push(
                    this.createTimelineMarker('center', timelineElement.periodEnd, Math.min(Math.max(offset, 0), 100)),
                );
            }
        });

        if (studyEndDate) {
            timelineMarkers.push(this.createTimelineMarker('end', studyEndDate, 100));
        }

        return timelineMarkers;
    }

    private createTimelineMarker(alignment: TimelineMarkerAlignment, date: string, offset: number): TimelineMarker {
        const normalizedDate = moment(date).format(dateFormat);

        return {
            alignment,
            date: normalizedDate,
            dateTime: normalizedDate,
            labelOffset: 0,
            labelTop: timelineMarkerTopOffset,
            lane: 0,
            offset,
        };
    }

    private observeTimelineLayout(): void {
        if (!this.timelineResizeObserver || !this.timeline || !this.timelineMarkerLabels) {
            return;
        }

        this.timelineResizeObserver.disconnect();
        this.timelineResizeObserver.observe(this.timeline.nativeElement);
        this.timelineMarkerLabels.forEach((timelineMarkerLabel) => {
            this.timelineResizeObserver?.observe(timelineMarkerLabel.nativeElement);
        });
    }

    private scheduleTimelineMarkerLayout(): void {
        if (!this.timeline || !this.timelineMarkerLabels) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            if (this.timelineMarkerLayoutFrame !== null) {
                window.cancelAnimationFrame(this.timelineMarkerLayoutFrame);
            }

            this.timelineMarkerLayoutFrame = window.requestAnimationFrame(() => this.updateTimelineMarkerLayout());
        });
    }

    private updateTimelineMarkerLayout(): void {
        this.timelineMarkerLayoutFrame = null;

        if (!this.timeline || !this.timelineMarkerLabels) {
            return;
        }

        const timelineMarkerLabels = this.timelineMarkerLabels.toArray();
        const timelineWidth = this.timeline.nativeElement.getBoundingClientRect().width;

        if (timelineMarkerLabels.length !== this.timelineMarkers.length || timelineWidth <= 0) {
            return;
        }

        const maximumLabelWidth = Math.floor(timelineWidth);

        if (this.timelineMarkerMaximumLabelWidth !== maximumLabelWidth) {
            this.ngZone.run(() => {
                this.timelineMarkerMaximumLabelWidth = maximumLabelWidth;
                this.cdr.detectChanges();
            });
        }

        if (timelineMarkerLabels.length === 0) {
            this.ngZone.run(() => {
                this.timelineMarkersPositioned = true;
                this.cdr.markForCheck();
            });
            return;
        }

        const labelRectangles = timelineMarkerLabels.map((timelineMarkerLabel) =>
            timelineMarkerLabel.nativeElement.getBoundingClientRect(),
        );
        const markerLayout = calculateTimelineMarkerLayout(
            this.timelineMarkers,
            labelRectangles.map((labelRectangle) => labelRectangle.width),
            timelineWidth,
        );
        const maximumLane = markerLayout.reduce(
            (currentMaximum, timelineMarker) => Math.max(currentMaximum, timelineMarker.lane),
            0,
        );
        const laneHeights = Array.from({ length: maximumLane + 1 }, () => timelineMarkerLaneHeight);

        markerLayout.forEach((timelineMarker, index) => {
            laneHeights[timelineMarker.lane] = Math.max(
                laneHeights[timelineMarker.lane],
                Math.ceil(labelRectangles[index].height) + timelineMarkerLaneGap,
            );
        });

        let nextLaneTop = timelineMarkerTopOffset;
        const laneTops = laneHeights.map((laneHeight) => {
            const laneTop = nextLaneTop;
            nextLaneTop += laneHeight;
            return laneTop;
        });

        this.ngZone.run(() => {
            this.timelineMarkers = this.timelineMarkers.map((timelineMarker, index) => ({
                ...timelineMarker,
                ...markerLayout[index],
                labelTop: laneTops[markerLayout[index].lane],
            }));
            this.timelineMarkerRailHeight = nextLaneTop;
            this.timelineMarkersPositioned = true;
            this.cdr.markForCheck();
        });
    }

    // This method will generate and return the timeline elements for the regimes data
    private getTimelineElements(regimes: Regime[], studyStartDate: string, studyEndDate: string): TimelineElement[] {
        const studyLength = this.getDays(
            moment(studyEndDate).endOf('day').valueOf() - moment(studyStartDate).startOf('day').valueOf(),
        );
        return regimes
            .filter((r) => r.periodStart && r.periodEnd)
            .map((r) => {
                return {
                    name: r.name,
                    size: this.getTimelineSize(r, studyLength),
                    periodEnd: r.periodEnd,
                };
            });
    }

    // This method will calculate and return the timeline size
    private getTimelineSize(regime: Regime, studyLength: number): number {
        const periodLength = this.getDays(
            moment(regime.periodEnd).endOf('day').valueOf() - moment(regime.periodStart).startOf('day').valueOf(),
        );
        return (periodLength * 100) / studyLength;
    }

    // This method converts milliseconds to days, rounding to the nearest full day
    private getDays(milliseconds: number): number {
        return milliseconds / millisecondsInDay;
    }
}
